import clsx from 'clsx'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useParams } from 'react-router-dom'
import { catchError, EMPTY, finalize, from, takeUntil } from 'rxjs'
import { CampaignSubmissionApi } from 'src/api'
import {
  Button,
  Checkbox,
  Spinner
} from 'src/components'
import { CAMPAIGN_SUBMISSION_STATUS_TEXT } from 'src/constants'
import { ECampaignStatus, ECampaignSubmissionStatus } from 'src/enums'
import {
  useAsRef,
  useBehaviorMapper,
  useDebouncedLoading,
  useUnsubscribe
} from 'src/hooks'
import { IconApplicantNotAMatchIllustration, IconApplicantPossibleMatchIllustration, IconApplicantToReviewIllustration, IconClose, IconEmployersStroke, IconFindIllustration, IconPencilStroke, IconSearch, IconZeroApplicant } from 'src/icons'
import { IconApplicantGreatMatchIllustration } from 'src/icons/icon-applicant-great-match-illustration'
import { ICampaignModel, ICampaignSubmissionModel } from 'src/interfaces'
import { CampaignApplicantCard } from 'src/partials/card/campaign-applicant-card'
import { ModalApplicantBulkActionReview } from 'src/partials/modal-applicant-bulk-action-review'
import { ModalApplicantConfirmPassed } from 'src/partials/modal-applicant-confirm-passed'
import { ModalApplicantDetail } from 'src/partials/modal-applicant-detail'
import { ModalSendMessage } from 'src/partials/modal-send-message'
import { browserHistory, ERoutes, generate } from 'src/router'
import { DialogService, LoadMoreService, SnackbarService } from 'src/services'
import { AuthModule, LoadingModule } from 'src/store'
import { ECampaignRoutes } from '../../routes.enum'
import Style from './style.module.scss'

interface IEmptyProps {
  isJobPublished: boolean
  count: number
  tab?: ECampaignSubmissionStatus
  campaignId?: string
}

const Empty = ({ isJobPublished, count, tab, campaignId }: IEmptyProps) => {
  if (count) {
    return (
      <div className="fx-1 fx-column fx-center gap-10  pt-15">
        <IconFindIllustration/>
        <div className="fx-column fx-center gap-5">
          <span className="heading-24 txt-black-01">No Matches Found</span>
          <span className="txt-black-02">Try changing up your filters to explore more potential fits.</span>
        </div>
      </div>
    )
  }

  if (tab === ECampaignSubmissionStatus.TO_REVIEW) {
    return (
      <div className="fx-1 fx-column fx-center gap-10 heading-24 txt-black-01 pt-15">
        <IconApplicantToReviewIllustration/>

        <span className="ff-rubik bold-24">No Applicant to Review Yet</span>

        <Button className="fx gap-2 mt-1" onClick={() => browserHistory.push(generate([ECampaignRoutes.CAMPAIGNS_APPLICANTS, { campaignId }], { tab: ECampaignSubmissionStatus.ALL }))}>
          <IconEmployersStroke/>
          View All Applicants
        </Button>
      </div>
    )
  }

  if ([ECampaignSubmissionStatus.GREAT_MATCH, ECampaignSubmissionStatus.POSSIBLE_MATCH, ECampaignSubmissionStatus.NOT_A_MATCH, ECampaignSubmissionStatus.PASSED].includes(tab as ECampaignSubmissionStatus)) {
    const icon: Partial<Record<ECampaignSubmissionStatus, React.ReactNode>> = {
      [ECampaignSubmissionStatus.GREAT_MATCH]: <IconApplicantGreatMatchIllustration/>,
      [ECampaignSubmissionStatus.PASSED]: <IconApplicantGreatMatchIllustration style={{ transform: 'rotate(-180deg)' }}/>,
      [ECampaignSubmissionStatus.POSSIBLE_MATCH]: <IconApplicantPossibleMatchIllustration/>,
      [ECampaignSubmissionStatus.NOT_A_MATCH]: <IconApplicantNotAMatchIllustration/>
    }

    return (
      <div className="fx-1 fx-column fx-center gap-10 heading-24 txt-black-01 pt-15">
        {icon[tab as ECampaignSubmissionStatus]}

        <span className="ff-rubik bold-24">No Applicant Here Yet</span>

        <Button className="fx gap-2 mt-1" onClick={() => browserHistory.push(generate([ECampaignRoutes.CAMPAIGNS_APPLICANTS, { campaignId }], { tab: ECampaignSubmissionStatus.TO_REVIEW }))}>
          <IconEmployersStroke/>
          Review Applicants
        </Button>
      </div>
    )
  }

  return (
    <div className="fx-1 fx-column fx-center gap-10 heading-24 txt-black-01 pt-15">
      <IconZeroApplicant/>

      {isJobPublished
        ? (
          <span>No Applicant Yet</span>
        )
        : (
          <span className="text-center">
            Once Verified, You'll See Applicants <br/>
            in this Dashboard
          </span>
        )}

      <Button className="fx gap-2 mt-1" onClick={() => browserHistory.push(generate(ERoutes.TALENT_VIBE_TALENT))}>
        <IconEmployersStroke/>
        View Talent Marketplace
      </Button>
    </div>
  )
}

interface IProps {
  tab?: ECampaignSubmissionStatus
  count?: number
  campaign?: ICampaignModel
  _loadMoreService: LoadMoreService<ICampaignSubmissionModel>
  countSubmission: () => void
}

export const ApplicantItems: FC<IProps> = ({ tab, count, countSubmission, _loadMoreService, campaign }) => {
  const unsubscribe$ = useUnsubscribe()
  const { campaignId } = useParams<{ campaignId: string }>()
  const isAuthenticated = useBehaviorMapper(AuthModule.isAuthenticated$)

  const { ref: refLoadMore } = useInView({ threshold: 0 })
  const loading = useBehaviorMapper(_loadMoreService.loading$)
  const items = useBehaviorMapper(_loadMoreService.items$)

  const debounceLoading = useDebouncedLoading(!items?.length && !!loading)

  const [selectedItems, setSelectedItems] = useState<
    ICampaignSubmissionModel[]
  >([])

  useEffect(() => {
    if (campaignId && !loading) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, campaignId, loading])

  const [currentIndex, setCurrentIndex] = useState(-1)
  const canPreviousProps = useMemo(() => currentIndex > 0, [currentIndex])
  const canNextProps = useMemo(
    () => currentIndex + 1 < items?.length,
    [currentIndex, items?.length]
  )
  console.log(currentIndex, items, canPreviousProps, canNextProps)

  const onDetail = useCallback((index: number) => {
    setCurrentIndex(index)
  }, [])

  const onPrevious = useCallback(() => setCurrentIndex((prev) => prev - 1), [])

  const onNext = useCallback(() => {
    setCurrentIndex((prev) => prev + 1)

    if (currentIndex === items.length - 2 && isAuthenticated) {
      _loadMoreService.loadMore()
    }
  }, [_loadMoreService, currentIndex, isAuthenticated, items.length])

  const onSelectedItem = useCallback(
    (campaignSubmission: ICampaignSubmissionModel) => {
      if (selectedItems?.includes(campaignSubmission)) {
        setSelectedItems?.(
          selectedItems.filter(({ id }) => id !== campaignSubmission.id)
        )
        return
      }

      setSelectedItems?.([...(selectedItems || []), campaignSubmission])
    },
    [selectedItems, setSelectedItems]
  )

  // Modal
  const propsRef = useAsRef({
    isSlide: true,
    submission: items[currentIndex],
    canPreviousProps,
    canNextProps,
    onPrevious,
    onNext,
    campaign,
    onChange: (...[id, values]: Parameters<typeof _loadMoreService.updateOneItem>) => id && _loadMoreService.updateOneItem(id, values)
  })
  const modalRef = useRef<ReturnType<typeof DialogService.open>>()
  useEffect(() => {
    if (currentIndex <= -1) {
      if (modalRef.current) {
        modalRef.current.unmount()
        modalRef.current = undefined
      }
      return
    }
    if (modalRef.current) {
      modalRef.current.props(propsRef.current)
      return
    }
    modalRef.current = DialogService.open(ModalApplicantDetail, {
      ...propsRef.current,
      onUnmounted: () => {
        setCurrentIndex(-1)
        modalRef.current = undefined
      }
    })
  }, [currentIndex, propsRef])

  // update campaign submission status
  const onMultipleReview = useCallback((status: ECampaignSubmissionStatus) => {
    LoadingModule.toggle(true)
    const ids = selectedItems.map(({ id }) => id)

    from(CampaignSubmissionApi.multipleReview({ ids, status }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(() => {
        _loadMoreService.removeItems(ids)
        countSubmission()
        setSelectedItems([])
        SnackbarService.success(`Applicant(s) added to ${CAMPAIGN_SUBMISSION_STATUS_TEXT[status as ECampaignSubmissionStatus]}.`)
      })
  }, [selectedItems, unsubscribe$, _loadMoreService, countSubmission])

  const onMultiplePass = useCallback((rejectionReason?: string) => {
    LoadingModule.toggle(true)
    const ids = selectedItems.map(({ id }) => id)

    from(CampaignSubmissionApi.multiplePass({ ids, rejectionReason }))
      .pipe(
        takeUntil(unsubscribe$),
        catchError((error) => {
          SnackbarService.axiosError(error)
          return EMPTY
        }),
        finalize(() => LoadingModule.toggle(false))
      )
      .subscribe(() => {
        _loadMoreService.removeItems(ids)
        countSubmission()
        setSelectedItems([])
        SnackbarService.success('Applicant(s) added to Passed.')
      })
  }, [selectedItems, unsubscribe$, _loadMoreService, countSubmission])

  // clear selected items when tab changes
  useEffect(() => {
    if (tab) {
      setSelectedItems([])
    }
  }, [tab])

  return (
    <>
      {debounceLoading && (
        <div className="fx-1 fx fx-center">
          <Spinner/>
        </div>
      )}

      <div
        style={{
          height: debounceLoading ? 0 : undefined,
          opacity: debounceLoading ? 0 : undefined
        }}
        className={clsx('px-8', !selectedItems?.length && 'pb-8')}
      >
        {!items?.length
          ? <Empty count={count || 0} tab={tab} campaignId={campaignId} isJobPublished={campaign?.status === ECampaignStatus.PUBLISHED}/>
          : (
            <div className="fx fx-wrap-wrap gap-4">
              {items?.map((item, index) => (
                <CampaignApplicantCard
                  key={item.id}
                  tab={tab}
                  submission={item}
                  checked={selectedItems?.includes(item)}
                  onSelectedItem={() => onSelectedItem?.(item)}
                  onChange={(id, values) => id && _loadMoreService.updateOneItem(id, values)}
                  onDetail={() => onDetail?.(index)}
                  onRemove={(id) => id && _loadMoreService.removeItems(id)}
                  countSubmission={countSubmission}
                />
              ))}
              <div ref={refLoadMore}/>
            </div>
          )}
      </div>

      {!!selectedItems.length && (
        <div className={Style.action}>
          <div className="fx fx-ai-center gap-3">
            <Checkbox
              className="mr-4"
              label="Select All"
              checked={selectedItems.length === items.length}
              onChange={() =>
                selectedItems.length === items.length
                  ? setSelectedItems([])
                  : setSelectedItems(items)}
            />

            <Button
              className="fx fx-ai-center gap-2"
              disabled={loading || !selectedItems.length}
              onClick={() =>
                DialogService.open(ModalSendMessage, {
                  initialCampaignSubmissions: selectedItems,
                  onRemoveWrapperMultiMessage: (campaignSubmissions: ICampaignSubmissionModel[]) => setSelectedItems(campaignSubmissions)
                })}
            >
              <IconPencilStroke color="#fff"/>
              Write Message
            </Button>

            {![ECampaignSubmissionStatus.ALL, ECampaignSubmissionStatus.PASSED].includes(tab as ECampaignSubmissionStatus) && (
              <Button
                variant="secondary"
                className="fx fx-ai-center gap-2"
                style={{ backgroundColor: '#72DA97' }}
                onClick={() =>
                  DialogService.open(ModalApplicantBulkActionReview, {
                    tab,
                    action: onMultipleReview
                  })}
              >
                <IconSearch size={20}/>
                Review
              </Button>
            )}

            {tab === ECampaignSubmissionStatus.NOT_A_MATCH && (
              <Button
                variant="secondary"
                onClick={() => DialogService.open(ModalApplicantConfirmPassed, { isBulkAction: true, action: onMultiplePass })}
                style={{ backgroundColor: '#FBC3BC' }}
              >
                <div className="fx fx-center" style={{ height: 16 }}>
                  <IconClose size={20}/>
                </div>
                Pass
              </Button>
            )}

            <Button
              variant="secondary"
              disabled={loading}
              onClick={() => setSelectedItems([])}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
